import { useImperativeHandle, useRef, useState } from 'react';
import type { FormEvent, MouseEvent, Ref } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { LoadingFailedBox, NgButton, NgDataListSelect, NgModal } from '@/ui';
import type { NgModalHandle } from '@/ui';
import { getPortalAvailableCollaborationAgencies, requestCollaboration } from '@ngsos/portal-core/commands';
import type { AvailableCollaborationAgency } from '@ngsos/portal-core/commands';
import { formatAgencyNameWithDistance } from '@ngsos/portal-core/incidents';

const FORM_ID = 'add-agency-to-incident-form';

export type AddAgencyToIncidentDialogHandle = {
  showModal: () => void;
};

export type AddAgencyToIncidentDialogProps = {
  incidentId: string;
  ref?: Ref<AddAgencyToIncidentDialogHandle>;
};

export function AddAgencyToIncidentDialog({ incidentId, ref }: AddAgencyToIncidentDialogProps) {
  const { t, i18n } = useTranslation();
  const modalRef = useRef<NgModalHandle>(null);

  const [opening, setOpening] = useState(0);
  const [selectedAgency, setSelectedAgency] = useState<AvailableCollaborationAgency | null>(null);

  const availableAgencies = useQuery({
    queryKey: ['incidents', incidentId, 'available-collaboration-agencies', opening],
    queryFn: () => getPortalAvailableCollaborationAgencies(incidentId),
    enabled: opening > 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const closeDialog = () => {
    modalRef.current?.hide();
  };

  const invitation = useMutation({
    mutationFn: (agency: AvailableCollaborationAgency) => requestCollaboration(incidentId, agency.id),
    onSuccess: () => closeDialog(),
    onError: () => availableAgencies.refetch(),
  });

  const agencies = availableAgencies.data ?? [];
  const isLoadingAgencies = availableAgencies.isFetching;
  const noAgenciesToAdd = agencies.length === 0;

  useImperativeHandle(ref, () => ({
    showModal: () => {
      setSelectedAgency(null);
      invitation.reset();
      setOpening((previousOpening) => previousOpening + 1);
      modalRef.current?.show();
    },
  }));

  const addAgencyToIncident = () => {
    if (!isLoadingAgencies && !invitation.isPending && selectedAgency != null) {
      invitation.mutate(selectedAgency);
    }
  };

  const submitAddAgencyToIncident = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    addAgencyToIncident();
  };

  const addAgencyToIncidentClicked = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    addAgencyToIncident();
  };

  return (
    <NgModal
      ref={modalRef}
      header={t('AddAgencyToIncidentDialog_Header')}
      testID="add-agency-dialog"
      bodyTemplate={
        isLoadingAgencies ? (
          <div className="placeholder-glow" role="status">
            <div className="placeholder col-3 mb-2"></div>
            <div className="placeholder col-12 py-3"></div>
            <span className="visually-hidden">{t('Loading_GeneralLoadingMessage')}</span>
          </div>
        ) : availableAgencies.isError ? (
          <LoadingFailedBox onRetry={() => void availableAgencies.refetch()} />
        ) : (
          <>
            {invitation.isError && (
              <div className="alert alert-danger" role="alert">
                {t('Global_ActionFailed')}
              </div>
            )}
            {noAgenciesToAdd ? (
              <div className="border border-1 border-dark-subtle border-dashed p-3 text-black-50 bg-light small text-center">
                {t('AddAgencyToIncidentDialog_AllAgenciesAlreadyAdded')}
              </div>
            ) : (
              <form id={FORM_ID} onSubmit={submitAddAgencyToIncident}>
                <NgDataListSelect<AvailableCollaborationAgency>
                  items={agencies}
                  value={selectedAgency ?? undefined}
                  onValueChanged={(value) => {
                    setSelectedAgency(value ?? null);
                    invitation.reset();
                  }}
                  textSelector={(agency) =>
                    formatAgencyNameWithDistance(
                      agency.displayName,
                      agency.distanceFromIncidentInMeters,
                      i18n.language,
                    )
                  }
                  nullText={t('AddAgencyToIncidentDialog_SelectAgencyPlaceholderText')}
                  label={t('AddAgencyToIncidentDialog_SelectAgencyToAddLabel')}
                  testID="add-agency-dialog-agency-input"
                  labelTestID="add-agency-dialog-agency-label"
                />
              </form>
            )}
          </>
        )
      }
      footerTemplate={
        <>
          {!isLoadingAgencies && !availableAgencies.isError && !noAgenciesToAdd && (
            <NgButton
              submitButton
              form={FORM_ID}
              icon={invitation.isPending ? 'fal fa-spinner-third fa-spin' : 'fal fa-share-from-square'}
              text={t('AddAgencyToIncidentDialog_Button_AddAgency')}
              disabled={invitation.isPending || selectedAgency == null}
              showProgress
              enableMultipleClickPrevention
              testID="add-agency-dialog-share-button"
              onClick={addAgencyToIncidentClicked}
            />
          )}
          <NgButton text={t('Button_Close')} outline onClick={closeDialog} />
        </>
      }
    />
  );
}
